<div class="nacha-review-step">
  <!-- Summary stats -->
  <div class="review-stats">
    <div class="stat-item">
      <span class="stat-value">{{ matches.length }}</span>
      <span class="stat-label">Total Entries</span>
    </div>
    <div class="stat-item stat-debit">
      <span class="stat-value">{{ debitCount }}</span>
      <span class="stat-label">Debits</span>
    </div>
    <div class="stat-item stat-credit">
      <span class="stat-value">{{ creditCount }}</span>
      <span class="stat-label">Credits</span>
    </div>
    <div class="stat-item stat-ready">
      <span class="stat-value">{{ readyMatches.length }}</span>
      <span class="stat-label">Ready</span>
    </div>
    <div class="stat-item stat-attention">
      <span class="stat-value">{{ attentionMatches.length }}</span>
      <span class="stat-label">Needs Attention</span>
    </div>
    @if (totalReadyAmount > 0) {
    <div class="stat-item stat-total">
      <span class="stat-value">{{ totalReadyAmount | currency }}</span>
      <span class="stat-label">Selected Total</span>
    </div>
    }
  </div>

  <!-- Ready to Process -->
  @if (readyMatches.length > 0) {
  <div class="table-section ready-section">
    <h4>
      <mat-icon class="section-icon success">check_circle</mat-icon>
      Ready to Process ({{ includedCount }} of {{ readyMatches.length }} selected)
    </h4>
    <table mat-table [dataSource]="readyMatches" class="review-table">
      <!-- Include checkbox -->
      <ng-container matColumnDef="include">
        <th mat-header-cell *matHeaderCellDef>Include</th>
        <td mat-cell *matCellDef="let match">
          <mat-checkbox
            [checked]="match.include"
            (change)="onToggleInclude(match)"
            [matTooltip]="match.include ? 'Exclude from processing' : 'Include in processing'"
          ></mat-checkbox>
        </td>
      </ng-container>

      <!-- Individual ID -->
      <ng-container matColumnDef="individualId">
        <th mat-header-cell *matHeaderCellDef>Individual ID</th>
        <td mat-cell *matCellDef="let match">{{ match.row.individualIdNumber }}</td>
      </ng-container>

      <!-- Customer Name -->
      <ng-container matColumnDef="customerName">
        <th mat-header-cell *matHeaderCellDef>Customer</th>
        <td mat-cell *matCellDef="let match">
          {{ match.customer?.displayName || match.customer?.companyName || match.row.individualName }}
        </td>
      </ng-container>

      <!-- Masked Bank Account -->
      <ng-container matColumnDef="maskedAccount">
        <th mat-header-cell *matHeaderCellDef>ACH Account</th>
        <td mat-cell *matCellDef="let match">
          <span
            class="ach-badge"
            [matTooltip]="
              match.accountSource === 'freely'
                ? 'Charging the account on file'
                : 'Charging the account from the NACHA file'
            "
          >
            <mat-icon>account_balance</mat-icon>
            {{ chargedAccount(match) }}
            @if (match.accountSource === 'freely') {
            <span class="source-tag freely">On file</span>
            } @else if (match.accountSource === 'nacha') {
            <span class="source-tag nacha">From file</span>
            }
          </span>
        </td>
      </ng-container>

      <!-- Amount -->
      <ng-container matColumnDef="amount">
        <th mat-header-cell *matHeaderCellDef>Amount</th>
        <td mat-cell *matCellDef="let match">{{ match.row.amount | currency }}</td>
      </ng-container>

      <!-- Transaction Type -->
      <ng-container matColumnDef="txType">
        <th mat-header-cell *matHeaderCellDef>Type</th>
        <td mat-cell *matCellDef="let match">
          <span class="tx-type-badge" [class.debit]="match.row.isDebit" [class.credit]="!match.row.isDebit">
            {{ match.row.transactionTypeLabel }}
          </span>
        </td>
      </ng-container>

      <!-- Trace Number -->
      <ng-container matColumnDef="traceNumber">
        <th mat-header-cell *matHeaderCellDef>Trace #</th>
        <td mat-cell *matCellDef="let match" class="trace-number">{{ match.row.traceNumber }}</td>
      </ng-container>

      <!-- Status -->
      <ng-container matColumnDef="status">
        <th mat-header-cell *matHeaderCellDef>Status</th>
        <td mat-cell *matCellDef="let match">
          <span class="status-badge" [class]="getStatusClass(match)">
            <mat-icon>{{ getStatusIcon(match) }}</mat-icon>
            {{ getStatusLabel(match) }}
          </span>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="readyDisplayedColumns"></tr>
      <tr mat-row *matRowDef="let row; columns: readyDisplayedColumns" [class.excluded]="!row.include"></tr>
    </table>
  </div>
  }

  <!-- Needs Attention -->
  @if (attentionMatches.length > 0) {
  <div class="table-section attention-section">
    <h4>
      <mat-icon class="section-icon warning">warning</mat-icon>
      Needs Attention ({{ attentionMatches.length }})
    </h4>

    <!-- Bulk: create customers for every "Customer not found" row -->
    @if (noCustomerCount > 1) {
    <div class="bulk-actions">
      <span class="bulk-label">
        {{ noCustomerCount }} customers not found:
      </span>
      <button
        mat-stroked-button
        color="primary"
        (click)="onAddAllCustomers()"
        matTooltip="Create a new customer from the NACHA data for every unmatched row"
      >
        <mat-icon>group_add</mat-icon>
        Add All Customers
      </button>
    </div>
    }

    <!-- Bulk "apply to all" for account-mismatch rows -->
    @if (mismatchCount > 1) {
    <div class="bulk-actions">
      <span class="bulk-label">
        Apply to all {{ mismatchCount }} account mismatches:
      </span>
      <button
        mat-stroked-button
        (click)="onApplyToAll('nacha')"
        matTooltip="Charge every mismatched customer using the account in the NACHA file"
      >
        <mat-icon>description</mat-icon>
        Use All NACHA
      </button>
      <button
        mat-stroked-button
        (click)="onApplyToAll('freely')"
        matTooltip="Charge every mismatched customer using their account on file"
      >
        <mat-icon>account_balance</mat-icon>
        Use All Freely
      </button>
      <button
        mat-stroked-button
        color="primary"
        (click)="onApplyToAll('update')"
        matTooltip="Replace each on-file account with the NACHA account, then charge it"
      >
        <mat-icon>sync</mat-icon>
        Update All Freely
      </button>
    </div>
    }

    <table mat-table [dataSource]="attentionMatches" class="review-table attention-table">
      <!-- Individual ID -->
      <ng-container matColumnDef="individualId">
        <th mat-header-cell *matHeaderCellDef>Individual ID</th>
        <td mat-cell *matCellDef="let match">{{ match.row.individualIdNumber }}</td>
      </ng-container>

      <!-- Individual Name -->
      <ng-container matColumnDef="individualName">
        <th mat-header-cell *matHeaderCellDef>Name (from file)</th>
        <td mat-cell *matCellDef="let match">{{ match.row.individualName }}</td>
      </ng-container>

      <!-- Amount -->
      <ng-container matColumnDef="amount">
        <th mat-header-cell *matHeaderCellDef>Amount</th>
        <td mat-cell *matCellDef="let match">{{ match.row.amount | currency }}</td>
      </ng-container>

      <!-- Transaction Type -->
      <ng-container matColumnDef="txType">
        <th mat-header-cell *matHeaderCellDef>Type</th>
        <td mat-cell *matCellDef="let match">
          <span class="tx-type-badge" [class.debit]="match.row.isDebit" [class.credit]="!match.row.isDebit">
            {{ match.row.transactionTypeLabel }}
          </span>
        </td>
      </ng-container>

      <!-- Account detail: on-file vs file (mismatch rows only) -->
      <ng-container matColumnDef="accountDetail">
        <th mat-header-cell *matHeaderCellDef>Account (on file vs file)</th>
        <td mat-cell *matCellDef="let match">
          @if (match.status === 'account-mismatch') {
          <div class="account-compare">
            <span class="account-line on-file">
              <span class="account-tag">On file</span>
              <mat-icon>account_balance</mat-icon>
              {{ maskAccount(onFileToken(match)?.account) }}
              <span class="routing">· {{ onFileToken(match)?.abaRoute }}</span>
            </span>
            <span class="account-line in-file">
              <span class="account-tag">In file</span>
              <mat-icon>description</mat-icon>
              {{ maskAccount(match.row.bankAccountNumber) }}
              <span class="routing">· {{ match.row.routingNumber }}</span>
            </span>
          </div>
          } @else {
          <span class="account-line in-file">
            <mat-icon>description</mat-icon>
            {{ maskAccount(match.row.bankAccountNumber) }}
            <span class="routing">· {{ match.row.routingNumber }}</span>
          </span>
          }
        </td>
      </ng-container>

      <!-- Status -->
      <ng-container matColumnDef="status">
        <th mat-header-cell *matHeaderCellDef>Issue</th>
        <td mat-cell *matCellDef="let match">
          <span class="status-badge" [class]="getStatusClass(match)">
            <mat-icon>{{ getStatusIcon(match) }}</mat-icon>
            {{ getStatusLabel(match) }}
          </span>
        </td>
      </ng-container>

      <!-- Actions -->
      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef>Actions</th>
        <td mat-cell *matCellDef="let match">
          <div class="action-buttons">
            @switch (match.status) { @case ('no-customer') {
            <button
              mat-stroked-button
              color="primary"
              (click)="onAddCustomer(match)"
              matTooltip="Create a new customer pre-filled with NACHA data"
            >
              <mat-icon>person_add</mat-icon>
              Add Customer
            </button>
            <button mat-stroked-button (click)="onSelectCustomer(match)" matTooltip="Select an existing customer">
              <mat-icon>manage_search</mat-icon>
              Select Customer
            </button>
            } @case ('account-mismatch') {
            <button
              mat-stroked-button
              (click)="onUseNacha(match)"
              matTooltip="Charge the account from the NACHA file (one-time)"
            >
              <mat-icon>description</mat-icon>
              Use NACHA
            </button>
            <button
              mat-stroked-button
              (click)="onUseFreely(match)"
              matTooltip="Charge the customer's account on file"
            >
              <mat-icon>account_balance</mat-icon>
              Use Freely
            </button>
            <button
              mat-stroked-button
              color="primary"
              (click)="onUpdateFreely(match)"
              matTooltip="Replace the on-file account with the NACHA account, then charge it"
            >
              <mat-icon>sync</mat-icon>
              Update Freely
            </button>
            } @case ('no-account') {
            <button
              mat-stroked-button
              color="primary"
              (click)="onSaveOnFile(match)"
              matTooltip="Save the NACHA account to the customer's file, then charge it"
            >
              <mat-icon>save</mat-icon>
              Save to File
            </button>
            <button
              mat-stroked-button
              (click)="onUseNacha(match)"
              matTooltip="Charge the account from the NACHA file without saving it"
            >
              <mat-icon>description</mat-icon>
              Use NACHA
            </button>
            } }
          </div>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="attentionDisplayedColumns"></tr>
      <tr mat-row *matRowDef="let row; columns: attentionDisplayedColumns"></tr>
    </table>
  </div>
  } @if (matches.length === 0) {
  <div class="empty-state">
    <mat-icon>inbox</mat-icon>
    <p>No entries to review.</p>
  </div>
  }
</div>
